<template>
  <div
    class="pt-2 slide-image transition-instruction-bar"
    :class="{
      'has-bg-image': slideActive.backgroundImage,
      'has-caption': !slideActive.title || slideActive.title == ' ',
    }"
  >
    <div class="flex image-body">
      <div class="iframe-wrapper">
        <div class="youtube-player-wrap"></div>
        <div :id="videoId" class="youtube-iframe"></div>
      </div>
    </div>
    <div
      class="p-0 caption"
      v-if="hasSlideActiveTitle"
      :class="{
        'small-text': slideActive.title && slideActive.title.length > 64
      }"
    >
      <SlideTitleEditor
        class="p-0 youtube-caption-text"
        :class="{'has-background': hasSlideActiveTitle}"
        type="slide-title"
      />
    </div>
  </div>
</template>
<script>
import _debounce from 'lodash/debounce'
import { mapGetters, mapState } from 'vuex'
import Util from '@/util'
import SlideTitleEditor from '@/components-v2/presentation-editor/middle/SlideTitleEditor.vue'

export default {
  components: {
    SlideTitleEditor,
  },
  data() {
    return {
      playerWrap: null,
      youtubePlayer: null,
    }
  },
  computed: {
    ...mapGetters(['isPresenting']),
    ...mapState({
      slideActive: (state) => state.slideActive,
    }),
    hasSlideActiveTitle() {
      return this.slideActive.title && this.slideActive.title.trim().length
    },
    videoId() {
      return this.getYoutubeVideoId(this.slideActive.youtubeLink)
    },
  },
  watch: {
    videoId(newVideo, oldVideo) {
      if (newVideo !== oldVideo) {
        this.setupYoutubePlayer()
      }
    },
    isPresenting(newVideo, oldVideo) {
      if (newVideo !== oldVideo) {
        this.setupYoutubePlayer()
      }
    },
  },

  mounted() {
    this.playerWrap = document.querySelector('.youtube-player-wrap')
    this.setupYoutubePlayer()
  },

  destroyed() {
    this.resetYoutubeIframe()
  },

  methods: {
    setupYoutubePlayer() {
      if (window.YT) {
        this.updateYoutubeVideo()
      } else {
        this.loadYoutubeEmbedScript()
        this.listenYoutubeApiReadyEvent()
      }
    },

    loadYoutubeEmbedScript() {
      if (typeof window.YT === 'undefined') {
        const tag = document.createElement('script')
        tag.src = 'https://www.youtube.com/iframe_api'
        const firstScriptTag = document.getElementsByTagName('script')[0]
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
      }
    },
    getYoutubeVideoId(youtubeLink) {
      if (!youtubeLink) return null
      // there're 2 cases for the links. We should handle both cases to get the correct videoId
      // 1. https://www.youtube.com/watch?v=ZQ7oqmikZDQ
      // 2. https://www.youtube.com/embed/ZQ7oqmikZDQ
      if (youtubeLink.includes('watch?v')) {
        return Util.getParamValue('v', youtubeLink)
      }
      if (youtubeLink.includes('embed')) {
        const embedIndex = youtubeLink.indexOf('/embed/')
        return youtubeLink.substring(embedIndex + 7)
      }
      return null
    },

    updateYoutubeVideo() {
      if (!this.videoId) return
      this.resetYoutubeIframe()
      this.injectYoutubeIframe()
      this.playerWrap.addEventListener('click', () => {
        const playerState = this.youtubePlayer.getPlayerState()
        if (playerState == window.YT.PlayerState.ENDED) {
          this.youtubePlayer.seekTo(0)
        } else if (playerState == window.YT.PlayerState.PAUSED) {
          this.youtubePlayer.playVideo()
        }
      })
    },

    resetYoutubeIframe() {
      if (this.youtubePlayer) {
        this.youtubePlayer.destroy()
        this.youtubePlayer = null
      }
    },

    injectYoutubeIframe() {
      this.playerWrap.classList.remove('ended')
      this.playerWrap.classList.remove('paused')
      if (this.youtubePlayer) {
        this.youtubePlayer.cueVideoById(this.videoId)
      } else {
        this.$nextTick(() => {
          this.youtubePlayer = new window.YT.Player(this.videoId, {
            width: 906,
            height: 510,
            videoId: this.videoId,
            allow: 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture',
            events: {
              onStateChange: this.onPlayerStateChange,
            },
          })
        })
      }
    },

    // Need to debounce here, because seeking the video will also trigger the pause event
    // and we don't want to show the pause icon when seeking
    onPlayerStateChange: _debounce(function debounceChangeState(event) {
      if (event.data == window.YT.PlayerState.ENDED) {
        this.playerWrap.classList.add('ended')
      } else if (event.data == window.YT.PlayerState.PAUSED) {
        this.playerWrap.classList.add('paused')
      } else if (event.data == window.YT.PlayerState.PLAYING) {
        this.playerWrap.classList.remove('ended')
        this.playerWrap.classList.remove('paused')
      }
    }, 100, { leading: false, trailing: true }),

    listenYoutubeApiReadyEvent() {
      const self = this
      window.onYouTubeIframeAPIReady = function listenYoutubeFrame() {
        self.updateYoutubeVideo()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.has-bg-image {
  .caption {
    color: #fff;
  }

  .youtube-caption-text {
    &::v-deep p {
      width: fit-content;
      border-radius: 50px;
    }
    &.has-background::v-deep p {
      background-color: #00000061;
    }
  }
}

.youtube-player-wrap {
  width: 908px;
  height: 511px;
  position: absolute;
  cursor: pointer;
  background-color: black;
  background-repeat: no-repeat;
  background-position: center;
  display: none;
}

.iframe-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.youtube-player-wrap.ended {
  display: block;
  background-size: 64px 64px;
  background-image: url(data:image/svg+xml;utf8;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiB2aWV3Qm94PSIwIDAgNTEwIDUxMCI+PHBhdGggZD0iTTI1NSAxMDJWMEwxMjcuNSAxMjcuNSAyNTUgMjU1VjE1M2M4NC4xNSAwIDE1MyA2OC44NSAxNTMgMTUzcy02OC44NSAxNTMtMTUzIDE1My0xNTMtNjguODUtMTUzLTE1M0g1MWMwIDExMi4yIDkxLjggMjA0IDIwNCAyMDRzMjA0LTkxLjggMjA0LTIwNC05MS44LTIwNC0yMDQtMjA0eiIgZmlsbD0iI0ZGRiIvPjwvc3ZnPg==);
}

.youtube-player-wrap.paused {
  display: block;
  background-size: 40px 40px;
  background-image: url(data:image/svg+xml;utf8;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZlcnNpb249IjEiIHdpZHRoPSIxNzA2LjY2NyIgaGVpZ2h0PSIxNzA2LjY2NyIgdmlld0JveD0iMCAwIDEyODAgMTI4MCI+PHBhdGggZD0iTTE1Ny42MzUgMi45ODRMMTI2MC45NzkgNjQwIDE1Ny42MzUgMTI3Ny4wMTZ6IiBmaWxsPSIjZmZmIi8+PC9zdmc+);
}
.caption {
  display: flex;
  justify-content: center;
  &::v-deep p {
    padding: 8px 16px;
  }
}

.image-body {
  position: relative;
  padding-top: 16px;
}

@media (max-width: 768px) and (orientation: landscape) {
  .image-body {
    padding-top: 36.569%;
  }
}
</style>
